import { useState } from 'react'

/**
 * SummaryBulletList — renders a list of win/flag bullets with colored
 * source pills and author attribution.
 *
 * Used in two places:
 *   - Live exec summary (read mode + edit mode)
 *   - Weekly archive snapshot (read mode only, via `readOnly` prop)
 *
 * Props:
 *   bullets    — array of { text, sourceType, sourceLabel, author }
 *   accent     — hex color for the bullet dot and section header.
 *                Wins: teal/green (#0F6E56). Flags: deep red (#A32D2D).
 *   editing    — whether we're in edit mode
 *   onChange   — (nextBullets) => void, fired when any bullet edit occurs
 *   emptyLabel — read-mode text when bullets is empty (e.g. "No wins
 *                called out this week.")
 *
 * Edit UX: each bullet is a row with an editable text input, a readonly
 * source pill, a readonly author chip (edit those through regenerate),
 * and an × to remove. Below the list: "+ Add bullet" appends a new row
 * with empty text and "other/Manual" source metadata.
 */
export default function SummaryBulletList({
  bullets,
  accent,
  editing = false,
  onChange,
  emptyLabel = 'Nothing to call out.',
}) {
  if (!editing) {
    if (!bullets || bullets.length === 0) {
      return (
        <div
          style={{
            fontSize: 12,
            color: 'var(--muted-light)',
            fontStyle: 'italic',
            padding: '4px 0',
          }}
        >
          {emptyLabel}
        </div>
      )
    }
    return (
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bullets.map((b, i) => (
          <BulletRow key={i} bullet={b} accent={accent} />
        ))}
      </ul>
    )
  }

  // Edit mode
  return <EditableBullets bullets={bullets || []} accent={accent} onChange={onChange} />
}

/**
 * A single read-mode bullet row: colored dot, text, source pill, author.
 * Bullets without a sourceLabel (e.g. user-added bullets during edit)
 * render with no pill so they don't look fabricated.
 */
function BulletRow({ bullet, accent }) {
  const palette = pilletteFor(bullet.sourceType)
  return (
    <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span
        style={{
          flexShrink: 0,
          width: 4,
          height: 4,
          borderRadius: '50%',
          background: accent,
          marginTop: 8,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text)' }}>
          {bullet.text}
        </div>
        {(bullet.sourceLabel || bullet.author) && (
          <div
            style={{
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              marginTop: 4,
              flexWrap: 'wrap',
            }}
          >
            {bullet.sourceLabel && (
              <span
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: palette.color,
                  background: palette.bg,
                  padding: '1px 7px',
                  borderRadius: 99,
                }}
              >
                {bullet.sourceLabel}
              </span>
            )}
            {bullet.author && (
              <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                · {bullet.author}
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  )
}

/**
 * Edit-mode bullet list — each row is an editable text input plus
 * readonly source/author metadata plus an × remove button. Adding a
 * new bullet appends a row with empty text and "Manual" source tag.
 */
function EditableBullets({ bullets, accent, onChange }) {
  const setBullet = (i, patch) => {
    const next = bullets.map((b, j) => (i === j ? { ...b, ...patch } : b))
    onChange(next)
  }
  const remove = (i) => {
    onChange(bullets.filter((_, j) => j !== i))
  }
  const add = () => {
    onChange([
      ...bullets,
      {
        text: '',
        sourceType: 'other',
        sourceLabel: 'Manual',
        author: '',
      },
    ])
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {bullets.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--muted)', fontStyle: 'italic', padding: '4px 0' }}>
          No bullets yet — click "+ Add bullet" to write one.
        </div>
      )}
      {bullets.map((b, i) => {
        const palette = pilletteFor(b.sourceType)
        return (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              padding: '6px 8px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: '#fafafa',
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 4,
                height: 4,
                borderRadius: '50%',
                background: accent,
                marginTop: 10,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                type="text"
                value={b.text}
                onChange={(e) => setBullet(i, { text: e.target.value })}
                placeholder="Type a bullet…"
                style={{
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  color: 'var(--text)',
                  padding: 0,
                  outline: 'none',
                }}
              />
              {(b.sourceLabel || b.author) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 6,
                    alignItems: 'center',
                    marginTop: 3,
                    flexWrap: 'wrap',
                  }}
                >
                  {b.sourceLabel && (
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: palette.color,
                        background: palette.bg,
                        padding: '1px 7px',
                        borderRadius: 99,
                      }}
                    >
                      {b.sourceLabel}
                    </span>
                  )}
                  {b.author && (
                    <span style={{ fontSize: 10, color: 'var(--muted)' }}>
                      · {b.author}
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              title="Remove bullet"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--muted-light)',
                fontSize: 14,
                cursor: 'pointer',
                padding: 0,
                lineHeight: 1,
                marginTop: 4,
              }}
            >
              ×
            </button>
          </div>
        )
      })}
      <button
        type="button"
        onClick={add}
        style={{
          alignSelf: 'flex-start',
          background: 'transparent',
          border: '1px dashed var(--border)',
          color: 'var(--muted)',
          padding: '4px 10px',
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        + Add bullet
      </button>
    </div>
  )
}

/**
 * pilletteFor — color palette for a source pill by type.
 * - goal  → blue (specific goal weekly summary)
 * - team  → green (team DRI update)
 * - other → gray (cross-goal pattern, reporting gap, HubSpot, manual)
 * Used in both read and edit mode so the visual cue stays consistent.
 */
function pilletteFor(sourceType) {
  if (sourceType === 'goal') return { color: '#185FA5', bg: '#E6F1FB' }
  if (sourceType === 'team') return { color: '#0F6E56', bg: '#E1F5EE' }
  return { color: '#5F5E5A', bg: '#F1EFE8' }
}
